import React, { useState } from 'react';
import { IonPicker, IonPickerColumn, IonPickerColumnOption } from '@ionic/react';
import moment from 'moment';
import { Box, Button, Dialog, DialogActions, FormHelperText, TextField, Typography } from '@mui/material';

const HOURS = Array.from({ length: 12 }, (_, i) => ({ text: String(i + 1), value: i + 1 }));
const MINUTES = Array.from({ length: 60 }, (_, i) => ({ text: String(i).padStart(2, '0'), value: i }));
const AMPMS = [
  { text: 'AM', value: 'AM' },
  { text: 'PM', value: 'PM' },
];

const toMoment = (time) => {
  const isPM = time.ampm === 'PM';
  const hour24 = isPM ? (time.hour === 12 ? 12 : time.hour + 12) : time.hour === 12 ? 0 : time.hour;

  return moment().hour(hour24).minute(time.minute).second(0).millisecond(0);
};

const fromMoment = (m) => {
  const h = m ? m.hour() : 8;
  return {
    hour: h % 12 || 12,
    minute: m ? m.minute() : 0,
    ampm: h < 12 ? 'AM' : 'PM',
  };
};

const NativeMinMaxTimePicker = ({ label, value, onChange, showFieldErrors, showWarning, errorText = '' }) => {
  const [open, setOpen] = useState(false);
  const [time, setTime] = useState(fromMoment(value));

  const handleCancel = () => setOpen(false);

  const handleOpen = () => {
    setTime(fromMoment(value));
    setOpen(true);
  };

  const handleConfirm = () => {
    onChange(toMoment(time));
    setOpen(false);
  };

  const displayValue = value ? value.format('hh:mm A') : '--:-- --';

  return (
    <Box my={1}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body1">{label}</Typography>
        <TextField
          value={displayValue}
          size="small"
          sx={{ maxWidth: '110px' }}
          onClick={handleOpen}
          inputProps={{ readOnly: true }}
          error={(errorText && showFieldErrors) || showWarning}
        />
      </Box>
      <Box>
        <FormHelperText error={(errorText && showFieldErrors) || showWarning}>{errorText}</FormHelperText>
      </Box>
      <Dialog open={open} onClose={handleConfirm} PaperProps={{ sx: { borderRadius: 3, width: 320 } }}>
        <IonPicker>
          <IonPickerColumn
            value={time.hour}
            onIonChange={(e) => setTime((prev) => ({ ...prev, hour: e.detail.value }))}
          >
            {HOURS.map((h) => (
              <IonPickerColumnOption key={`hour-${h.value}`} value={h.value}>
                {h.text}
              </IonPickerColumnOption>
            ))}
          </IonPickerColumn>
          <IonPickerColumn
            value={time.minute}
            onIonChange={(e) => setTime((prev) => ({ ...prev, minute: e.detail.value }))}
          >
            {MINUTES.map((m) => (
              <IonPickerColumnOption key={`minute-${m.value}`} value={m.value}>
                {m.text}
              </IonPickerColumnOption>
            ))}
          </IonPickerColumn>
          <IonPickerColumn
            value={time.ampm}
            onIonChange={(e) => setTime((prev) => ({ ...prev, ampm: e.detail.value }))}
          >
            {AMPMS.map((ampm) => (
              <IonPickerColumnOption key={ampm.value} value={ampm.value}>
                {ampm.text}
              </IonPickerColumnOption>
            ))}
          </IonPickerColumn>
        </IonPicker>

        <DialogActions>
          <Button onClick={handleCancel} autoFocus>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NativeMinMaxTimePicker;
